import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { TedAnswer } from "@/components/TedAnswer";
import { Sparkles, Send, Loader2, Trash2, Plus, AlertTriangle, CalendarClock, MessageSquare, X, Maximize2, Minimize2, GripVertical } from "lucide-react";

function Citations({ items, onNavigate }) {
  if (!items?.length) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-1" data-testid="ted-citations">
      {items.slice(0, 24).map((c) => (
        <Link key={`${c.type}:${c.id}`} to={c.link || "#"} className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
          data-testid={`ted-citation-${c.type}-${c.id}`} title={c.label} onClick={onNavigate}>{c.type} {c.id}</Link>
      ))}
    </div>
  );
}

function Proposal({ p, onDone }) {
  const [state, setState] = useState(!p.status || p.status === "pending" ? null : p.status);
  const decide = async (d) => {
    try { await api.post(`/ted/proposals/${p.id}/${d}`); setState(d === "confirm" ? "confirmed" : "rejected"); toast.success(d === "confirm" ? "Report schedule created" : "Proposal rejected"); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
  };
  return (
    <div className="mt-2 border border-amber-200 bg-amber-50 rounded-md p-2 text-xs" data-testid={`ted-proposal-${p.id}`}>
      <div className="flex items-center gap-1 font-semibold text-amber-800"><CalendarClock className="w-3.5 h-3.5" /> {state ? ({ confirmed: "Confirmed", rejected: "Rejected" }[state] || "Expired") : "Pending your approval"}</div>
      <div className="text-amber-900">{p.payload?.title} → {(p.payload?.recipients || []).join(", ")}</div>
      {!state && (
      <div className="mt-1 flex gap-2">
        <Button size="sm" className="h-7" onClick={() => decide("confirm")} data-testid={`ted-proposal-confirm-${p.id}`}>Confirm</Button>
        <Button size="sm" variant="ghost" className="h-7" onClick={() => decide("reject")} data-testid={`ted-proposal-reject-${p.id}`}>Reject</Button>
      </div>
      )}
    </div>
  );
}

const Em = ({ t }) => <>{t.split(/(?<!\*)\*(?!\*)/).map((p, j) => (j % 2 ? <em key={j}>{p}</em> : <React.Fragment key={j}>{p}</React.Fragment>))}</>;
const Rich = ({ text }) => <>{String(text || "").split("**").map((p, i) => (i % 2 ? <strong key={i}><Em t={p} /></strong> : <Em key={i} t={p} />))}</>;

function Bubble({ m, onDone, onNavigate }) {
  const mine = m.role === "user";
  if (!mine && m.structured && !m.error) {
    return (
      <div className="flex gap-2.5" data-testid="ted-msg-assistant">
        <span className="mt-0.5 h-7 w-7 shrink-0 rounded-full bg-gradient-to-br from-cyan-500 to-[var(--ted-violet)] grid place-items-center"><Sparkles className="w-3.5 h-3.5 text-white" /></span>
        <div className="min-w-0 flex-1">
          <TedAnswer st={m.structured} onNavigate={onNavigate} />
          {(m.proposals || []).map((p) => <Proposal key={p.id} p={p} onDone={onDone} />)}
        </div>
      </div>
    );
  }
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`ted-msg-${m.role}`}>
      <div className={`max-w-[92%] rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap ${mine ? "bg-slate-900 text-white" : m.error ? "bg-rose-50 text-rose-700" : "bg-slate-100 text-slate-800"}`}>
        <Rich text={m.text} />
        {!!m.unverified_numbers?.length && (
          <div className="mt-2 flex items-start gap-1 text-[11px] text-amber-700" data-testid="ted-unverified">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0" /> Not traced to a record: {m.unverified_numbers.join(", ")}. Treat as unverified.
          </div>
        )}
        <Citations items={m.citations} onNavigate={onNavigate} />
        {(m.proposals || []).map((p) => <Proposal key={p.id} p={p} onDone={onDone} />)}
      </div>
    </div>
  );
}

function History({ items, onOpen, onDelete, active }) {
  return (
    <div className="border-b max-h-20 sm:max-h-36 overflow-y-auto" data-testid="ted-history">
      {items.map((c) => (
        <div key={c.id} className={`flex items-center gap-2 px-3 py-1.5 text-xs ${active === c.id ? "bg-indigo-50" : "hover:bg-slate-50"}`}>
          <button className="flex-1 text-left truncate" onClick={() => onOpen(c.id)} data-testid={`ted-conv-${c.id}`}>
            <MessageSquare className="inline w-3 h-3 mr-1 text-slate-400" />{c.title}
          </button>
          <button onClick={() => onDelete(c.id)} aria-label="Delete conversation" data-testid={`ted-conv-delete-${c.id}`}><Trash2 className="w-3.5 h-3.5 text-slate-400 hover:text-rose-600" /></button>
        </div>
      ))}
    </div>
  );
}

export function TedChatPanel({ status, onNavigate, placeholder, testid = "ted-chat" }) {
  const [convs, setConvs] = useState([]);
  const [convId, setConvId] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const end = useRef(null);
  const box = useRef(null);
  const loadConvs = useCallback(() => { api.get("/ted/conversations").then((r) => setConvs(r.data.items)).catch(() => {}); }, []);
  useEffect(() => { loadConvs(); }, [loadConvs]);
  useEffect(() => { if (box.current) { box.current.scrollTop = box.current.scrollHeight; } }, [msgs, busy]);
  const [autoSend, setAutoSend] = useState(false);
  useEffect(() => {
    const h = (e) => { setConvId(null); setMsgs([]); setInput(e.detail?.prompt || ""); setAutoSend(!!e.detail?.send); };
    window.addEventListener("ted:prefill", h);
    return () => { window.removeEventListener("ted:prefill", h); };
  }, []);
  const openConv = async (id) => {
    const r = await api.get(`/ted/conversations/${id}`);
    setConvId(id);
    setMsgs(r.data.messages);
  };
  const del = async (id) => {
    if (!window.confirm("Delete this conversation permanently?")) return;
    try { await api.delete(`/ted/conversations/${id}`); if (id === convId) { setConvId(null); setMsgs([]); } loadConvs(); toast.success("Conversation deleted"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not delete"); }
  };
  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", text }]);
    setBusy(true);
    try {
      const { data } = await api.post("/ted/chat", { message: text, conversation_id: convId });
      setConvId(data.conversation_id);
      setMsgs((m) => [...m, { role: "assistant", text: data.answer, citations: data.citations, unverified_numbers: data.unverified_numbers, proposals: data.proposals, structured: data.structured }]);
      loadConvs();
    } catch (e) {
      setMsgs((m) => [...m, { role: "assistant", text: e.response?.data?.detail || "Ted is unavailable.", error: true }]);
    } finally { setBusy(false); }
  };
  useEffect(() => {
    if (autoSend && input) { setAutoSend(false); send(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSend, input]);
  return (
    <div className="flex flex-col min-h-0 flex-1" data-testid={testid}>
        <div className="px-3 py-2 border-b flex justify-between items-center">
          <span className="text-xs font-semibold text-slate-500">Your conversations <span className="font-normal">· private, kept {status?.retention_days} days</span></span>
          <Button size="sm" variant="ghost" className="h-7" onClick={() => { setConvId(null); setMsgs([]); }} data-testid="ted-new-conversation"><Plus className="w-3.5 h-3.5 mr-1" /> New</Button>
        </div>
        <History items={convs} onOpen={openConv} onDelete={del} active={convId} />
        <div ref={box} className="flex-1 overflow-y-auto p-4 space-y-4 min-h-[200px] bg-[var(--ted-surface-muted,#F9F9FB)]" data-testid="ted-messages">
          {!msgs.length && <p className="text-xs text-slate-400">Try: “Which installations this week have a stock or loading problem?”</p>}
          {msgs.map((m, i) => <Bubble key={i} m={m} onDone={loadConvs} onNavigate={onNavigate} />)}
          {busy && <Loader2 className="w-4 h-4 animate-spin text-slate-400" data-testid="ted-thinking" />}
          <div ref={end} />
        </div>
        <div className="border-t p-3 flex gap-2" data-testid="ted-composer">
          <Textarea value={input} onChange={(e) => setInput(e.target.value)} rows={2} placeholder={placeholder || "Ask Ted…"} className="resize-none text-sm"
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} data-testid="ted-input" />
          <Button onClick={send} disabled={busy || !input.trim()} data-testid="ted-send"><Send className="w-4 h-4" /></Button>
        </div>
    </div>
  );
}

const MIN_W = 420, DEF_W = 700, WIDE_W = 950;

function useTedWidth() {
  const { user } = useAuth();
  const key = `ted.width.${user?.id || "anon"}`;
  const [w, setW] = useState(() => { const v = localStorage.getItem(key); return v === "full" ? "full" : Number(v) || DEF_W; });
  const save = useCallback((v) => { setW(v); localStorage.setItem(key, String(v)); }, [key]);
  return [w, save];
}

function SizeButton({ label, active, onClick, testid, children }) {
  return <button onClick={onClick} title={label} aria-label={label} data-testid={testid}
    className={`rounded-md px-2 py-1 text-[11px] font-medium transition-colors ${active ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-100"}`}>{children}</button>;
}

export function TedDrawer({ open, onOpenChange, status }) {
  const [w, setW] = useTedWidth();
  const drag = useRef(null);
  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") { onOpenChange(false); } };
    if (open) { window.addEventListener("keydown", esc); }
    return () => { window.removeEventListener("keydown", esc); };
  }, [open, onOpenChange]);
  const startDrag = (e) => {
    e.preventDefault();
    const move = (ev) => { drag.current = Math.max(MIN_W, Math.min(window.innerWidth - 80, window.innerWidth - ev.clientX)); document.getElementById("ted-panel").style.width = `${drag.current}px`; };
    const up = () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); if (drag.current) { setW(Math.round(drag.current)); } drag.current = null; };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };
  if (!open) return null;
  const full = w === "full";
  return createPortal(
    <>
      <div className="fixed inset-0 z-[60] bg-slate-900/20 backdrop-blur-[2px]" onClick={() => onOpenChange(false)} data-testid="ted-drawer-overlay" />
      <aside id="ted-panel" role="dialog" aria-label="Ask Ted" data-testid="ted-drawer"
        className="fixed inset-y-0 right-0 z-[61] flex flex-col bg-white shadow-2xl border-l border-slate-200 w-full max-w-full md:max-w-[calc(100vw-80px)] animate-in slide-in-from-right duration-200 [&_[data-testid=ted-composer]]:pb-16"
        style={{ width: window.innerWidth < 1024 ? "100%" : full ? "calc(100vw - 80px)" : `${w}px` }}>
        {!full && <div onPointerDown={startDrag} className="hidden lg:flex absolute left-0 top-0 h-full w-2 -ml-1 cursor-col-resize items-center justify-center group" data-testid="ted-resize-handle"><GripVertical className="w-3 h-6 text-slate-300 group-hover:text-[var(--ted-violet)]" /></div>}
        <header className="px-4 py-3 border-b flex items-center gap-2">
          <span className="h-7 w-7 rounded-full bg-gradient-to-br from-cyan-500 to-[var(--ted-violet)] grid place-items-center"><Sparkles className="w-3.5 h-3.5 text-white" /></span>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-900">Ask Ted</div>
            <p className="text-[11px] text-slate-500 truncate" data-testid="ted-domains">Read-only · {(status?.domains || []).join(", ") || "nothing yet"}</p>
          </div>
          <div className="ml-auto hidden lg:flex items-center gap-0.5 rounded-lg border p-0.5" data-testid="ted-size-controls">
            <SizeButton label="Standard width" active={w === DEF_W} onClick={() => setW(DEF_W)} testid="ted-size-standard">700</SizeButton>
            <SizeButton label="Wide" active={w === WIDE_W} onClick={() => setW(WIDE_W)} testid="ted-size-wide">950</SizeButton>
            <SizeButton label={full ? "Exit full screen" : "Full screen"} active={full} onClick={() => setW(full ? DEF_W : "full")} testid="ted-size-full">{full ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}</SizeButton>
          </div>
          <Link to="/ted" onClick={() => onOpenChange(false)} className="text-[11px] font-medium text-[var(--ted-violet)] hover:underline ml-auto lg:ml-1" data-testid="ted-open-command-centre">Command Centre</Link>
          <button onClick={() => onOpenChange(false)} aria-label="Close" className="ml-1 rounded-md p-1 text-slate-400 hover:bg-slate-100" data-testid="ted-drawer-close"><X className="w-4 h-4" /></button>
        </header>
        <TedChatPanel status={status} onNavigate={() => onOpenChange(false)} />
      </aside>
    </>,
    document.body,
  );
}
